import * as models from 'smg/customer-api/models';
import * as html from 'smg/html';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';

import i18n from '../../i18n';
import { UpdateSmartPayWarning } from '../smartpay';
import { State } from '../state';
import { CapitalizeFirstLetter, PaymentOptionTypeIDtoName } from '../util';
import { ApplyCardIssuer, PaymentPlan, PlanEntry, SplitCharging } from './plan';
import { ChargingNotice } from './split';

// UpdatePaymentSummary refreshes the "Payment" preview row in the order
// summary — method type, short descriptor (last-4, etc.), and amount.
export function UpdatePaymentSummary(state: State): void {
    const { dom } = state;
    const p = state.payment;
    UpdateSmartPayWarning(state);
    if (state.split) {
        drawSplitPreview(state);
        return;
    }
    const selected = Boolean(p.paymentOption?.id || p.paymentOptionTypeID);

    if (!selected) {
        dom.$paymentPreview.replaceChildren(html.FromString(
            '<div class="w-100 py-16 px-md-20 text-body-secondary text-center fs-4 fst-italic">No payment option selected</div>'));
        return;
    }

    dom.$paymentPreview.classList.value = 'preview px-md-17 pt-2 pb-10';
    dom.$paymentPreview.replaceChildren(template.Clone(dom.$paymentPreviewTemplate));
    const $inner = dom.$paymentPreview.querySelector('.payment-preview-inner-parent') as HTMLElement;
    const $row = p.paymentOption ? resolvedRow(state) : inProgressRow(state);
    $inner.appendChild($row);
    appendPurchaseOrderAttachments(state);
}

function drawSplitPreview(state: State): void {
    const { dom } = state;
    const plan = PaymentPlan(state);
    dom.$paymentPreview.classList.value = 'preview px-md-17 pt-2 pb-10';
    dom.$paymentPreview.replaceChildren(template.Clone(dom.$paymentPreviewTemplate));
    const heading = dom.$paymentPreview.querySelector('.payment-preview-heading');
    heading?.classList.remove('d-none');
    const title = dom.$paymentPreview.querySelector('.payment-preview-title');
    if (title) {
        title.textContent = i18n('checkoutSplitSummaryTitle', { count: String(plan.entries.length) });
    }
    const note = dom.$paymentPreview.querySelector('.payment-preview-note');
    if (note && SplitCharging(state)) {
        note.classList.remove('d-none');
        note.textContent = ChargingNotice(state);
    }
    const inner = dom.$paymentPreview.querySelector('.payment-preview-inner-parent');
    for (const entry of plan.entries) {
        inner?.appendChild(splitPreviewItem(state, entry));
    }
}

function splitPreviewItem(state: State, entry: PlanEntry): DocumentFragment {
    const item = template.Clone(state.dom.$paymentPreviewItemCC);
    setText(item, '.cc-last-four', entry.last4.padStart(4, '0'));
    ApplyCardIssuer(item, entry.first6);
    setText(item, '.payment-preview-item-amount', DollarFormat(entry.amount));
    const tag = item.querySelector('.payment-preview-tag');
    const detail = item.querySelector('.payment-preview-detail');
    if (entry.status === 'paid' && tag) {
        tag.className = 'tag tag-soft tag-upper tag-xs tag-success';
        tag.textContent = i18n('checkoutSplitPaid');
    } else if (entry.status === 'charging' && tag) {
        tag.className = 'd-inline-flex align-items-center gap-2 small text-primary fw-semibold';
        const spinner = document.createElement('span');
        spinner.className = 'spinner-border spinner-border-sm';
        spinner.setAttribute('role', 'status');
        tag.append(spinner, document.createTextNode(i18n('checkoutSplitChargingTag')));
    } else if (entry.status === 'waiting' && tag) {
        tag.className = 'small text-body-secondary';
        tag.textContent = i18n('checkoutSplitWaiting');
    } else if (entry.status === 'declined' && detail) {
        detail.classList.remove('d-none');
        detail.classList.add('text-danger');
        detail.textContent = i18n('checkoutSplitDeclined', { reason: entry.splitEntry?.reason || '' });
    } else if (entry.status === 'not-tried' && detail) {
        detail.classList.remove('d-none');
        detail.classList.add('text-body-secondary');
        detail.textContent = i18n('checkoutSplitNotTried');
    }
    return item;
}

function resolvedRow(state: State): DocumentFragment {
    const { dom, payment, totalAmount } = state;
    switch (payment.paymentOption?.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
        const $item = template.Clone(dom.$paymentPreviewItemBank);
        setText($item, '.bank-last-four', payment.paymentOption?.creditCardLast4?.toString() || '');
        setText($item, '.acct-type', CapitalizeFirstLetter(payment.paymentOption?.accountType || ''));
        setText($item, '.bank-type', CapitalizeFirstLetter(payment.paymentOption?.bankType || ''));
        setText($item, '.payment-preview-item-amount', DollarFormat(totalAmount));
        return $item;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
        const $item = template.Clone(dom.$paymentPreviewItemCC);
        setText($item, '.cc-last-four', payment.paymentOption?.creditCardLast4?.toString().padStart(4, '0') || '');
        ApplyCardIssuer($item, payment.paymentOption?.creditCardFirst6?.toString() ?? '');
        setText($item, '.payment-preview-item-amount', DollarFormat(totalAmount));
        return $item;
    }
    default: {
        const $item = template.Clone(dom.$paymentPreviewItemOther);
        setText($item, '.payment-preview-item-type', CapitalizeFirstLetter(PaymentOptionTypeIDtoName(payment.paymentOptionTypeID ?? payment.paymentOption?.paymentOptionTypeID)));
        setText($item, '.payment-preview-item-amount', DollarFormat(totalAmount));
        return $item;
    }
    }
}

function inProgressRow(state: State): DocumentFragment {
    const { dom, payment, totalAmount } = state;
    const $item = template.Clone(dom.$paymentPreviewItemOther);
    $item.querySelector<HTMLDivElement>('.in-progress')?.classList.remove('d-none');
    setText($item, '.payment-preview-item-type', PaymentOptionTypeIDtoName(payment.paymentOptionTypeID));
    setText($item, '.payment-preview-item-amount', DollarFormat(totalAmount));
    return $item;
}

function appendPurchaseOrderAttachments(state: State): void {
    if (state.payment.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
        return;
    }
    const $thumbnails = state.payment.uploader?.getThumbnailPreviews({
        fancyboxGroup: 'purchase-order-summary',
        readonly: true,
    }) ?? [];
    if (!$thumbnails.length) {
        return;
    }

    const $previewItem = state.dom.$paymentPreview.querySelector<HTMLElement>('.payment-preview-item');
    const $content = $previewItem?.querySelector<HTMLElement>(':scope > .w-100');
    if (!$previewItem || !$content) {
        return;
    }

    $previewItem.classList.remove('col');
    $previewItem.classList.add('col-12');
    $content.className = 'payment-preview-po-summary w-100 d-flex flex-column flex-md-row';

    const $details = document.createElement('div');
    $details.className = 'col-12 col-md-auto pb-5 pb-md-0 pe-md-8';
    $details.replaceChildren(...Array.from($content.childNodes));

    const $attachments = document.createElement('div');
    $attachments.className = 'payment-preview-attachments col-12 col-md-auto flex-shrink-1 pt-5 pt-md-0 ps-md-8';
    const $heading = document.createElement('h3');
    $heading.className = 'fs-4';
    $heading.textContent = 'Attachments';
    const $thumbnailContainer = document.createElement('div');
    $thumbnailContainer.className = 'uploader-container';
    const $thumbnailList = document.createElement('div');
    $thumbnailList.className = 'uploader-thumbnails';
    $thumbnailContainer.appendChild($thumbnailList);
    $attachments.appendChild($heading);
    $attachments.appendChild($thumbnailContainer);
    $content.replaceChildren($details, $attachments);

    $thumbnails.forEach($thumbnail => $thumbnailList.appendChild($thumbnail));
}

function setText($item: DocumentFragment, selector: string, value: string): void {
    const $el = $item.querySelector(selector) as HTMLSpanElement | null;
    if ($el) {
        $el.textContent = value;
    }
}
